export const REAR_LABEL = /rear|back|environment|traseir|tr[aá]s/i;
export const FRONT_LABEL = /front|user|frontal|frente|selfie/i;

const PERMISSION_DENIED = new Set([
  'NotAllowedError',
  'PermissionDeniedError',
  'SecurityError',
]);

export type CameraFacing = 'environment' | 'user' | 'unknown';

export interface VideoDevice {
  deviceId: string;
  kind: string;
  label: string;
}

export interface CameraTrackLike {
  label: string;
  getSettings: () => { deviceId?: string; facingMode?: string };
  stop: () => void;
}

export interface CameraStreamLike {
  getTracks: () => Array<{ stop: () => void }>;
  getVideoTracks: () => CameraTrackLike[];
}

export interface CameraMedia {
  enumerateDevices: () => Promise<VideoDevice[]>;
  getUserMedia: (constraints: MediaStreamConstraints) => Promise<CameraStreamLike>;
}

export function classifyLabel(label: string): CameraFacing {
  const rear = REAR_LABEL.test(label);
  const front = FRONT_LABEL.test(label);
  if (rear && !front) return 'environment';
  if (front && !rear) return 'user';
  return 'unknown';
}

export function classifyTrack(track: CameraTrackLike): CameraFacing {
  const facing = track.getSettings?.().facingMode;
  if (facing === 'environment' || facing === 'user') return facing;
  return classifyLabel(track.label || '');
}

export function pickRearDevice(devices: VideoDevice[], currentDeviceId?: string): VideoDevice | null {
  const videos = devices.filter((device) => (
    device.kind === 'videoinput'
    && device.deviceId
    && device.deviceId !== currentDeviceId
  ));

  return videos.find((device) => classifyLabel(device.label) === 'environment') ?? null;
}

export function rearVideoConstraints(mode: 'exact' | 'ideal'): MediaStreamConstraints {
  return {
    audio: false,
    video: {
      facingMode: { [mode]: 'environment' },
      height: { ideal: 720 },
      width: { ideal: 1280 },
    },
  };
}

function errorName(error: unknown): string {
  return error && typeof error === 'object' && 'name' in error
    ? String((error as { name?: string }).name)
    : '';
}

export function isCameraPermissionDenied(error: unknown): boolean {
  return PERMISSION_DENIED.has(errorName(error));
}

export function isSafariUserAgent(userAgent: string): boolean {
  if (/CriOS|FxiOS|EdgiOS|Chrome|Chromium|Android/i.test(userAgent)) return false;
  return /Safari/i.test(userAgent);
}

export async function openRearCamera(media: CameraMedia): Promise<CameraStreamLike> {
  let stream: CameraStreamLike;
  try {
    stream = await media.getUserMedia(rearVideoConstraints('exact'));
  } catch (error) {
    if (isCameraPermissionDenied(error)) throw error;
    stream = await media.getUserMedia(rearVideoConstraints('ideal'));
  }

  const track = stream.getVideoTracks()[0];
  if (!track || classifyTrack(track) !== 'user') return stream;

  const rear = pickRearDevice(await media.enumerateDevices(), track.getSettings().deviceId);
  if (!rear) return stream;

  try {
    const next = await media.getUserMedia({
      audio: false,
      video: {
        deviceId: { exact: rear.deviceId },
        height: { ideal: 720 },
        width: { ideal: 1280 },
      },
    });
    stream.getTracks().forEach((item) => item.stop());
    return next;
  } catch {
    return stream;
  }
}

export function openRearCameraFromNavigator(): Promise<CameraStreamLike> {
  return openRearCamera(navigator.mediaDevices);
}
